import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, message } from 'antd';
import moment from 'moment';
import DataTable from '../../components/data-table/data-table.component';
import portalAPI from '../../api/api';
import { CLAIM_SUBMISSION_LIST_URL } from '../../api/URLs';
import ViewClaimDetails from '../../components/drawer/view-claim-detail';
import { getColumnFilterProps } from '../../exports/filter-props';
import { useSelector } from 'react-redux';

const ClaimsList = ({ cusTableSetting, renderTableHeader, pageSize = 10 }) => {
    const [tableLoading, setTableLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const drawerDetailRef = useRef();

    const [filter, setFilter] = useState({
        status: [],
        sortKeys: { createdAt: "desc" },
        pageNo: 1,
        pageSize,
        count: 0,
    });

    const debounceTimerRef = useRef(null);

    const statusOptions = useMemo(() => ({
        'open': 'Open',
        'under-review': 'Under Review',
        'approved': 'Approved',
        'paid': 'Paid',
        'rejected': 'Rejected'
    }), []);

    const sortOrder = {
        'ascend': 'asc',
        'descend': 'desc',
        "asc": 'ascend',
        "desc": 'descend'
    };

    const fetchClaimSubmissionList = async (pageNo, pageSize, filter) => {
        setTableLoading(true);
        try {
            const body = {
                pageNo,
                pageSize,
                status: filter.status || [],
                sortKeys: filter.sortKeys,
            };
            const res = await portalAPI.post(CLAIM_SUBMISSION_LIST_URL, body);
            if (res.data?.data) {
                setTableData(res.data.data);
                setFilter((prev) => ({
                    ...prev,
                    count: res.data.count || 0,
                    pageNo,
                    pageSize,
                }));
            }
        } catch (error) {
            message.error(error.message || 'Failed to fetch claims');
        } finally {
            setTableLoading(false);
        }
    };

    // Fetch data on filter change (debounced)
    useEffect(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            fetchClaimSubmissionList(filter.pageNo, filter.pageSize, filter);
        }, 300);
        return () => clearTimeout(debounceTimerRef.current);
    }, [filter.status, filter.sortKeys, filter.pageNo, filter.pageSize]);

    const handleTableChange = (pagination, filters, sorter) => {
        let changeFilter = { ...filter, sortKeys: {} };
        if (sorter?.order) {
            changeFilter.sortKeys[sorter.field] = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        if (pagination?.current) {
            changeFilter.pageNo = pagination.current;
        }

        if (pagination?.pageSize) {
            changeFilter.pageSize = pagination.pageSize;
        }
        setFilter({ ...changeFilter });
    }

    const tableSetting = {
        ...cusTableSetting,
        size: 'middle',
        onChange: handleTableChange,
        columns: [
            {
                title: 'Claim No.',
                dataIndex: 'caseNumber',
                render: (caseNumber, data) => (
                    <Button
                        className="cus-button-color"
                        type="link"
                        onClick={() => drawerDetailRef.current.open({ caseNumber })}
                    >
                        {caseNumber}
                    </Button>
                ),
            },
            {
                title: 'Date Submitted',
                dataIndex: 'createdAt',
                sorter: true,
                sortOrder: sortOrder[filter?.sortKeys["createdAt"]] || null,
                width: 200,
                render: (date) => moment(date).format('MM/DD/YYYY') || 'N/A',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                ...getColumnFilterProps(
                    'status',
                    filter,
                    setFilter,
                    Object.entries(statusOptions).map(([value, label]) => ({ value, label }))
                ),
                render: (status) => statusOptions[status] || 'N/A',
            },
        ],
        customPagination: {
            current: filter.pageNo,
            pageSize: filter.pageSize,
            total: filter.count,
            showSizeChanger: true,
        },
    };

    return (
        <>
            <div className="dashboard-card">
                {renderTableHeader?.()}
                <DataTable data={tableData} settings={tableSetting} loading={tableLoading} />
            </div>
            <ViewClaimDetails ref={drawerDetailRef} />
        </>
    );
};

export default ClaimsList;